import { useState } from "react";
import { Upload, Camera, CheckCircle2, Loader2, X, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { API } from "../lib/api";

function Field({ k, label, value, onChange, type = "text", required }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">{label}{required && " *"}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(k, e.target.value)}
        required={required}
        data-testid={`request-${k}`}
        className="mt-2 w-full border border-zinc-300 px-4 py-3 focus:outline-none focus:border-[#16A34A]"
      />
    </div>
  );
}

export default function RequestService() {
  const [form, setForm] = useState({
    name: "", phone: "", email: "",
    caravan_make_model_year: "", location: "", preferred_time: "", expected_timeframe: "", description: ""
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const addFiles = (incoming) => {
    const arr = Array.from(incoming);
    setFiles([...files, ...arr].slice(0, 12));
  };
  const removeFile = (i) => setFiles(files.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.description) {
      toast.error("Please add your name, phone and a description"); return;
    }
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      files.forEach((f) => fd.append("files", f));
      const r = await fetch(`${API}/service-requests`, { method: "POST", body: fd });
      if (!r.ok) throw new Error(await r.text());
      setDone(true);
      toast.success("Sent — Nomad will be in touch shortly");
    } catch (err) {
      toast.error("Couldn't send. Try fewer / smaller photos.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <main className="bg-white min-h-screen">
        <div className="container mx-auto px-6 py-24 max-w-2xl text-center" data-testid="request-done">
          <CheckCircle2 className="w-16 h-16 text-[#16A34A] mx-auto" />
          <h1 className="font-display text-4xl font-black tracking-tighter mt-6">Thanks — we've got it.</h1>
          <p className="mt-4 text-zinc-600">One of the team will call you back within one business day to talk through your van. If you sent photos, they'll have already had a look.</p>
          <div data-testid="request-followup-note" className="mt-8 inline-block border border-[#16A34A]/40 bg-[#16A34A]/10 px-5 py-3 text-sm text-zinc-800 max-w-md">
            Need to add more photos or details later? Just submit this same form again and someone will reply to you shortly.
          </div>
        </div>
      </main>
    );
  }

  const onField = (k, v) => setForm({ ...form, [k]: v });

  return (
    <main className="bg-zinc-50 min-h-screen">
      <section className="bg-zinc-950 text-white">
        <div className="container mx-auto px-6 py-16 max-w-3xl">
          <div className="font-mono text-xs uppercase tracking-[0.25em] text-[#22C55E] mb-4 flex items-center gap-2">
            <PhoneCall className="w-3.5 h-3.5" /> Request a Service · Call Back
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter">Tell us what's going on.</h1>
          <p className="mt-4 text-zinc-300 max-w-xl">Leave a few details and we'll call you back to sort out the next steps. Photos really help us understand the job — please add some if you can.</p>
        </div>
      </section>

      <section className="container mx-auto px-6 py-12 max-w-3xl">
        <form onSubmit={submit} className="space-y-5 bg-white border border-zinc-200 p-6" data-testid="request-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field k="name" label="Your name" value={form.name} onChange={onField} required />
            <Field k="phone" label="Phone" value={form.phone} onChange={onField} required />
            <Field k="email" label="Email" type="email" value={form.email} onChange={onField} />
            <Field k="caravan_make_model_year" label="Caravan make / model / year" value={form.caravan_make_model_year} onChange={onField} />
            <Field k="location" label="Where is the van?" value={form.location} onChange={onField} />
            <Field k="preferred_time" label="Best time to call you" value={form.preferred_time} onChange={onField} />
          </div>

          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">Your expected time frame</label>
            <select
              value={form.expected_timeframe}
              onChange={(e) => setForm({ ...form, expected_timeframe: e.target.value })}
              data-testid="request-expected_timeframe"
              className="mt-2 w-full border border-zinc-300 px-4 py-3 bg-white focus:outline-none focus:border-[#16A34A]"
            >
              <option value="">When do you need this done?</option>
              <option value="As soon as possible">As soon as possible</option>
              <option value="Within a few days">Within a few days</option>
              <option value="Within a week or two">Within a week or two</option>
              <option value="Flexible / no rush">Flexible / no rush</option>
              <option value="Booking ahead for my next stop">Booking ahead for my next stop</option>
            </select>
            <p className="mt-2 text-xs text-zinc-500">Heads up: we generally can't do same-day mechanical servicing — parts often need to be couriered in overnight. If you need a quick turnaround, it's best to book in advance for your next stop.</p>
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">What do you need? *</label>
            <textarea
              rows={4}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              required
              data-testid="request-description"
              placeholder="Describe the issue or the work you'd like done…"
              className="mt-2 w-full border border-zinc-300 px-4 py-3 focus:outline-none focus:border-[#16A34A]"
            />
          </div>

          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#16A34A] mb-2 flex items-center gap-2">
              <Camera className="w-3.5 h-3.5" /> Photos help a lot ({files.length}/12) — optional but encouraged
            </div>
            <label className="border-2 border-dashed border-zinc-300 hover:border-[#16A34A] transition-colors p-8 flex flex-col items-center justify-center cursor-pointer">
              <Upload className="w-8 h-8 text-zinc-400 mb-3" />
              <span className="text-sm font-semibold text-zinc-700">Tap to add photos or a short video</span>
              <span className="text-xs text-zinc-500 mt-1">JPG · PNG · MP4 · up to 25MB each</span>
              <input
                type="file"
                multiple
                accept="image/*,video/mp4,video/quicktime,video/webm"
                onChange={(e) => addFiles(e.target.files)}
                data-testid="request-files"
                className="hidden"
              />
            </label>
            {files.length > 0 && (
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-2">
                {files.map((f, i) => (
                  <div key={i} className="border border-zinc-200 p-2 flex items-center gap-2 text-xs">
                    <span className="truncate flex-1">{f.name}</span>
                    <button type="button" onClick={() => removeFile(i)} data-testid={`request-file-remove-${i}`}><X className="w-3.5 h-3.5 text-zinc-500" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={submitting}
            data-testid="request-submit"
            className="w-full bg-[#16A34A] text-white px-6 py-4 font-semibold hover:bg-[#15803D] disabled:opacity-60 inline-flex items-center justify-center gap-2"
          >
            {submitting ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <>Request service / call back</>}
          </button>
        </form>
      </section>
    </main>
  );
}
